/**
 * Clustering Configuration Service
 * Loads and manages DBSCAN clustering parameters from JSON configuration
 */

export interface ClusteringZoomLevel {
  maxZoom: number;
  epsilon: number;
  minPoints: number;
  description: string;
}

export interface ClusteringFallback {
  epsilon: number;
  minPoints: number;
  description: string;
}

export interface ClusteringThreshold {
  maxZoomForClustering: number;
  description: string;
}

export interface ClusteringPerformance {
  maxStationsPerZoom: {
    low: number;
    medium: number;
    high: number;
  };
}

export interface PersonalisationConfig {
  explorationThresholdMiles: number;
  description: string;
}

export interface HDBSCANConfig {
  description: string;
  version: string;
  clusteringThreshold: ClusteringThreshold;
  minClusterSize: number;
  minSamples: number;
  tuningNotes?: Record<string, string>;
}

export interface ClusteringConfig {
  dbscan: {
    description: string;
    version: string;
    clusteringThreshold: ClusteringThreshold;
    zoomLevels: ClusteringZoomLevel[];
    fallback: ClusteringFallback;
    performance: ClusteringPerformance;
    tuningNotes?: {
      epsilon: string;
      minPoints: string;
      maxZoomForClustering: string;
      recommendations: string[];
    };
  };
  hdbscan: HDBSCANConfig;
  personalisation: PersonalisationConfig;
}

/**
 * Default configuration fallback if loading fails
 */
const DEFAULT_CONFIG: ClusteringConfig = {
  dbscan: {
    description: "Default DBSCAN clustering configuration (legacy)",
    version: "1.1.0-fallback",
    clusteringThreshold: {
      maxZoomForClustering: 12,
      description: "Default threshold - clustering stops above zoom level 12"
    },
    zoomLevels: [
      { maxZoom: 6, epsilon: 60, minPoints: 3, description: "Country overview" },
      { maxZoom: 8, epsilon: 80, minPoints: 4, description: "Regional view" },
      { maxZoom: 10, epsilon: 50, minPoints: 3, description: "City view" },
      { maxZoom: 11, epsilon: 30, minPoints: 2, description: "Detailed view" }
    ],
    fallback: {
      epsilon: 20,
      minPoints: 2,
      description: "High zoom fallback"
    },
    performance: {
      maxStationsPerZoom: {
        low: 1000,
        medium: 800,
        high: 500
      }
    }
  },
  hdbscan: {
    description: "Default HDBSCAN clustering configuration",
    version: "2.0.0-fallback",
    clusteringThreshold: {
      maxZoomForClustering: 13,
      description: "Clustering stops above zoom level 13"
    },
    minClusterSize: 3,
    minSamples: 2,
  },
  personalisation: {
    explorationThresholdMiles: 2,
    description: "Default personalisation threshold - 2 miles from user location before switching to exploration mode"
  }
};

class ClusteringConfigService {
  private config: ClusteringConfig | null = null;
  private loadPromise: Promise<ClusteringConfig> | null = null;

  /**
   * Load clustering configuration from JSON file
   */
  async loadConfig(): Promise<ClusteringConfig> {
    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Return cached config if already loaded
    if (this.config) {
      return this.config;
    }

    this.loadPromise = this._fetchConfig();
    
    try {
      this.config = await this.loadPromise;
      console.log('✅ Clustering configuration loaded:', this.config.dbscan.version);
      return this.config;
    } catch (error) {
      console.warn('⚠️ Failed to load clustering config, using defaults:', error);
      this.config = DEFAULT_CONFIG;
      return this.config;
    } finally {
      this.loadPromise = null;
    }
  }

  /**
   * Fetch configuration from public JSON file
   */
  private async _fetchConfig(): Promise<ClusteringConfig> {
    const response = await fetch('/config/clustering-config.json');
    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.status}`);
    }

    const config = await response.json();

    // Basic validation — require at least one of dbscan.zoomLevels or hdbscan
    if (!config.dbscan && !config.hdbscan) {
      throw new Error('Invalid configuration structure');
    }

    return config;
  }

  /**
   * Get DBSCAN parameters for a specific zoom level
   */
  async getParametersForZoom(zoomLevel: number): Promise<{
    epsilon: number;
    minPoints: number;
    description: string;
  }> {
    const config = await this.loadConfig();
    
    // Find matching zoom level configuration
    for (const level of config.dbscan.zoomLevels) {
      if (zoomLevel <= level.maxZoom) {
        console.log(`🎯 Config DBSCAN Parameters: zoom=${zoomLevel}, ε=${level.epsilon}px, minPts=${level.minPoints}, desc="${level.description}"`);
        return {
          epsilon: level.epsilon,
          minPoints: level.minPoints,
          description: level.description
        };
      }
    }

    // Use fallback for high zoom levels
    console.log(`🎯 Fallback DBSCAN Parameters: zoom=${zoomLevel}, ε=${config.dbscan.fallback.epsilon}px, minPts=${config.dbscan.fallback.minPoints}`);
    return {
      epsilon: config.dbscan.fallback.epsilon,
      minPoints: config.dbscan.fallback.minPoints,
      description: config.dbscan.fallback.description
    };
  }

  /**
   * Get performance settings
   */
  async getPerformanceSettings(): Promise<ClusteringPerformance> {
    const config = await this.loadConfig();
    return config.dbscan.performance;
  }

  /**
   * Get HDBSCAN parameters
   */
  async getHDBSCANParameters(): Promise<{ minClusterSize: number; minSamples: number }> {
    const config = await this.loadConfig();
    const hdbscan = config.hdbscan ?? DEFAULT_CONFIG.hdbscan;
    console.log(`🎯 HDBSCAN Parameters: minClusterSize=${hdbscan.minClusterSize}, minSamples=${hdbscan.minSamples}`);
    return { minClusterSize: hdbscan.minClusterSize, minSamples: hdbscan.minSamples };
  }

  /**
   * Get clustering threshold configuration (prefers hdbscan threshold if present)
   */
  async getClusteringThreshold(): Promise<number> {
    const config = await this.loadConfig();
    const clusteringThresholdConfig = config.dbscan.clusteringThreshold ?? config.hdbscan?.clusteringThreshold;
    const threshold = clusteringThresholdConfig?.maxZoomForClustering;
    console.log(`🔍 Clustering threshold: clustering stops at zoom > ${threshold}`);
    return threshold;
  }

  /**
   * Get personalisation threshold configuration
   */
  async getPersonalisationThreshold(): Promise<number> {
    const config = await this.loadConfig();
    const threshold = config.personalisation.explorationThresholdMiles;
    console.log(`🎯 Personalisation threshold: exploration mode at > ${threshold} miles from location`);
    return threshold;
  }

  /**
   * Get current configuration (sync, may return null if not loaded)
   */
  getCurrentConfig(): ClusteringConfig | null {
    return this.config;
  }

  /**
   * Check if configuration is loaded
   */
  isLoaded(): boolean {
    return this.config !== null;
  }

  /**
   * Reload configuration (useful for development/testing)
   */
  async reloadConfig(): Promise<ClusteringConfig> {
    this.config = null;
    this.loadPromise = null;
    return this.loadConfig();
  }
}

// Export singleton instance
export const clusteringConfigService = new ClusteringConfigService();

// Export for direct usage
export { ClusteringConfigService };